import { useEffect, useRef, useState } from "react";
import { Plus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DealTransformed } from "@/lib/schema/deal-schema";
import { FieldDef, filterableFields, getField } from "./fields";
import {
  FilterNode,
  FilterOperator,
  OPERATOR_LABELS,
  defaultOperatorFor,
  describeFilter,
  isValuelessOperator,
  operatorsFor,
  suggestValues,
} from "./filters";

/**
 * "+ Add filter", built from the field registry.
 *
 * Replaces `filter-sheet.tsx`, which had one hardcoded control per filter and a
 * matching property on a fixed `FilterValues` interface. The controls here are
 * generated from each field's type, so a field added to the registry is
 * immediately filterable and a saved view can hold any combination.
 */

/** The value control depends on the field's type, not on which field it is. */
function ValueInput({
  field,
  node,
  deals,
  onChange,
}: {
  field: FieldDef;
  node: FilterNode;
  deals: DealTransformed[];
  onChange: (value: unknown) => void;
}) {
  if (isValuelessOperator(node.op)) return null;

  if (field.type === "boolean") {
    return (
      <Select
        value={node.value === true ? "true" : "false"}
        onValueChange={(v) => onChange(v === "true")}
      >
        <SelectTrigger className="h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">Yes</SelectItem>
          <SelectItem value="false">No</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  if (field.type === "date") {
    const asDate =
      node.value instanceof Date
        ? node.value.toISOString().slice(0, 10)
        : typeof node.value === "string"
          ? node.value.slice(0, 10)
          : "";
    return (
      <Input
        type="date"
        className="h-8 text-xs"
        value={asDate}
        onChange={(e) =>
          onChange(e.target.value ? new Date(e.target.value) : undefined)
        }
      />
    );
  }

  if (field.type === "number" || field.type === "currency") {
    return (
      <Input
        type="number"
        className="h-8 text-xs"
        value={node.value === undefined ? "" : String(node.value)}
        onChange={(e) =>
          onChange(e.target.value === "" ? undefined : Number(e.target.value))
        }
      />
    );
  }

  // Multi-select for `in`, offering the values present in the loaded deals.
  // Only ever a hint — with a page in hand this cannot be the full set — so a
  // free-text box sits underneath for anything not listed.
  if (node.op === "in") {
    const selected = Array.isArray(node.value) ? (node.value as string[]) : [];
    const options = suggestValues(deals, field.id);

    return (
      <div className="space-y-1">
        <ScrollArea className="max-h-[160px]">
          <div className="space-y-0.5 pr-2">
            {options.map((option) => (
              <label
                key={option}
                className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-xs hover:bg-muted"
              >
                <Checkbox
                  checked={selected.includes(option)}
                  onCheckedChange={() =>
                    onChange(
                      selected.includes(option)
                        ? selected.filter((v) => v !== option)
                        : [...selected, option]
                    )
                  }
                />
                <span className="truncate">{option}</span>
              </label>
            ))}
            {options.length === 0 && (
              <p className="px-1 py-1 text-xs text-muted-foreground">
                No values in the loaded deals — type one below.
              </p>
            )}
          </div>
        </ScrollArea>
        <Input
          className="h-8 text-xs"
          placeholder="Add a value…"
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            const typed = (e.target as HTMLInputElement).value.trim();
            if (!typed || selected.includes(typed)) return;
            onChange([...selected, typed]);
            (e.target as HTMLInputElement).value = "";
          }}
        />
      </div>
    );
  }

  return (
    <Input
      className="h-8 text-xs"
      placeholder="Value"
      value={node.value === undefined ? "" : String(node.value)}
      onChange={(e) => onChange(e.target.value || undefined)}
    />
  );
}

function FilterRow({
  node,
  deals,
  onChange,
  onRemove,
}: {
  node: FilterNode;
  deals: DealTransformed[];
  onChange: (next: FilterNode) => void;
  onRemove: () => void;
}) {
  const field = getField(node.field);
  if (!field) return null;

  return (
    <div className="space-y-1.5 rounded-md border border-border p-2">
      <div className="flex items-center gap-1.5">
        <Select
          value={node.field}
          onValueChange={(fieldId) => {
            const next = getField(fieldId);
            if (!next) return;
            // Operators and values rarely carry across types, so reset both
            // rather than leaving "after 45" on a text field.
            onChange({
              field: fieldId,
              op: defaultOperatorFor(next.type),
              value: undefined,
            });
          }}
        >
          <SelectTrigger className="h-8 flex-1 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {filterableFields().map((f) => (
              <SelectItem key={f.id} value={f.id}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 shrink-0 p-0 text-muted-foreground"
          onClick={onRemove}
          aria-label={`Remove ${field.label} filter`}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <Select
        value={node.op}
        onValueChange={(op) =>
          onChange({
            ...node,
            op: op as FilterOperator,
            value: isValuelessOperator(op as FilterOperator)
              ? undefined
              : node.value,
          })
        }
      >
        <SelectTrigger className="h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {operatorsFor(field.type).map((op) => (
            <SelectItem key={op} value={op}>
              {OPERATOR_LABELS[op]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <ValueInput
        field={field}
        node={node}
        deals={deals}
        onChange={(value) => onChange({ ...node, value })}
      />
    </div>
  );
}

export function FilterBuilder({
  filters,
  onChange,
  deals,
}: {
  filters: FilterNode[];
  onChange: (filters: FilterNode[]) => void;
  deals: DealTransformed[];
}) {
  const [open, setOpen] = useState(false);

  // Now that the list scrolls, a new row lands below the fold and Add filter
  // reads as having done nothing. Follow it down.
  const endOfList = useRef<HTMLDivElement>(null);
  const previousCount = useRef(filters.length);

  useEffect(() => {
    if (filters.length > previousCount.current) {
      endOfList.current?.scrollIntoView({ block: "nearest" });
    }
    previousCount.current = filters.length;
  }, [filters.length]);

  const addFilter = () => {
    const first = filterableFields()[0];
    if (!first) return;
    onChange([
      ...filters,
      { field: first.id, op: defaultOperatorFor(first.type), value: undefined },
    ]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
          <Plus className="h-3.5 w-3.5" />
          Add filter
          {filters.length > 0 && (
            <span className="text-muted-foreground">({filters.length})</span>
          )}
        </Button>
      </PopoverTrigger>
      {/*
        Capped to the room Radix measured between the trigger and the edge of
        the viewport, because the popover grows by a ~90px row per filter and
        at four it ran off the bottom of the screen — taking "Add filter" with
        it, so the only way to add a fifth was to zoom out. The rows scroll and
        the button stays put, rather than scrolling the whole popover and
        leaving the button just as far out of reach.
      */}
      <PopoverContent
        align="start"
        className="flex w-72 flex-col p-2"
        style={{ maxHeight: "var(--radix-popover-content-available-height)" }}
      >
        {filters.length === 0 ? (
          <p className="px-1 py-2 text-xs text-muted-foreground">
            No filters yet.
          </p>
        ) : (
          // A plain overflow container, not ScrollArea: sized by flex rather
          // than a max-height, ScrollArea's viewport grew to its content and
          // the root clipped it, so past a few filters the rest could not be
          // scrolled to.
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="space-y-2 pr-2">
              {filters.map((node, index) => (
                <FilterRow
                  key={index}
                  node={node}
                  deals={deals}
                  onChange={(next) =>
                    onChange(filters.map((f, i) => (i === index ? next : f)))
                  }
                  onRemove={() =>
                    onChange(filters.filter((_, i) => i !== index))
                  }
                />
              ))}
              <div ref={endOfList} />
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="mt-2 h-8 w-full shrink-0 justify-start gap-1.5 text-xs"
          onClick={addFilter}
        >
          <Plus className="h-3.5 w-3.5" />
          Add filter
        </Button>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The applied-filter chips, so what is being filtered is visible without
 * opening anything.
 *
 * A row of its own rather than part of the toolbar's control group. Inline
 * beside Sort/AI/Columns the chips had nowhere to go: the control group cannot
 * shrink, the row cannot wrap, and four chips at up to 220px each overflowed
 * it — squeezing the search field to nothing and pushing the right-hand
 * controls off the edge. Here they wrap onto as many lines as they need and
 * nothing else on the screen moves.
 */
export function FilterChips({
  filters,
  onChange,
}: {
  filters: FilterNode[];
  onChange: (filters: FilterNode[]) => void;
}) {
  if (filters.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 px-6 pb-2.5">
      {filters.map((node, index) => (
        <button
          key={index}
          type="button"
          onClick={() => onChange(filters.filter((_, i) => i !== index))}
          className="inline-flex h-7 max-w-[260px] items-center gap-1.5 rounded-md border border-border bg-background px-2 text-xs text-foreground transition-colors hover:bg-foreground/[0.04]"
          title={`Remove: ${describeFilter(node)}`}
        >
          <span className="truncate">{describeFilter(node)}</span>
          <X className="h-3 w-3 shrink-0 text-muted-foreground" />
        </button>
      ))}

      {filters.length > 1 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground"
          onClick={() => onChange([])}
        >
          Clear all
        </Button>
      )}
    </div>
  );
}
